import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BadgeCheck, FileText } from 'lucide-react';
import { useApi } from '../../utils/useApi';

/** Black-circle Gympe avatar used in every WhatsApp preview surface. */
export function GympeAvatar({ sizeClassName = 'w-9 h-9' }: { sizeClassName?: string }) {
  return (
    <div className={`${sizeClassName} bg-black rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden`}>
      <span className="font-black tracking-tight text-[0.55em] leading-none">
        <span className="text-white">Gym</span><span className="text-lime-400">pe</span>
      </span>
    </div>
  );
}

/**
 * The real "Gympe / Gympe India ✓" contact header WhatsApp shows on every
 * business-initiated conversation — same across all preview surfaces so
 * gyms see exactly what their members will see.
 */
export function GympeChatHeader({ compact = true }: { compact?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <GympeAvatar sizeClassName={compact ? 'w-9 h-9' : 'w-11 h-11'} />
      <div className="min-w-0">
        <div className={`font-semibold text-gray-900 truncate ${compact ? 'text-sm' : 'text-base'}`}>Gympe</div>
        <div className="flex items-center gap-1 text-gray-500">
          <span className={compact ? 'text-xs' : 'text-sm'}>Gympe India</span>
          <BadgeCheck className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-sky-500 fill-sky-500`} strokeWidth={0} />
        </div>
      </div>
    </div>
  );
}

/**
 * The document-header attachment card WhatsApp shows above the message body
 * for templates with a DOCUMENT header (e.g. payment_received_with_file,
 * invoice_manual_send) — mirrors the real WhatsApp file-attachment bubble.
 */
export function DocumentPreviewCard({ filename }: { filename: string }) {
  return (
    <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3 mb-1.5">
      <div className="w-9 h-9 bg-rose-600 rounded-md flex items-center justify-center flex-shrink-0">
        <FileText className="w-5 h-5 text-white" strokeWidth={2} />
      </div>
      <div className="min-w-0">
        <div className="text-sm text-gray-900 font-medium truncate">{filename}</div>
        <div className="text-xs text-gray-500">PDF Document</div>
      </div>
    </div>
  );
}

/**
 * Render WhatsApp (Meta) text formatting: *bold*, _italic_, ~strikethrough~,
 * ```monospace```. Single-level, per Meta's own rendering rules.
 */
export function formatWhatsAppText(text: string): React.ReactNode {
  const pattern = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|```[^`]+```)/g;
  const parts = text.split(pattern);

  return parts.map((part, index) => {
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <strong key={index} className="font-semibold">{part.slice(1, -1)}</strong>;
    }
    if (part.startsWith('_') && part.endsWith('_') && part.length > 2) {
      return <em key={index}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('~') && part.endsWith('~') && part.length > 2) {
      return <s key={index}>{part.slice(1, -1)}</s>;
    }
    if (part.startsWith('```') && part.endsWith('```') && part.length > 6) {
      return <code key={index} className="font-mono text-xs">{part.slice(3, -3)}</code>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

/** Debounce a value; used so live previews don't refetch on every keystroke. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timeout);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Server-rendered template preview: substitutes {{N}} using the template's
 * parameterSchema — real gym name for gym_data, the admin's typed values for
 * user_provided — so every preview in the wizard resolves identically.
 */
export function useTemplatePreview(
  templateId: string | undefined,
  userParams: Record<string, string>,
  fallbackContent?: string
) {
  const { fetchWithAuth } = useApi();
  const debouncedParams = useDebouncedValue(userParams, 350);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['template-preview', templateId, debouncedParams],
    queryFn: async () => {
      // Sent as JSON, not bracket notation: qs would parse sample_params[2]=…
      // as a compacted array (["…"]), losing the "2" key.
      const filled = Object.fromEntries(
        Object.entries(debouncedParams || {}).filter(([, value]) => value)
      );
      const query = Object.keys(filled).length
        ? `?sample_params=${encodeURIComponent(JSON.stringify(filled))}`
        : '';
      return await fetchWithAuth(`/api/enhanced-templates/${templateId}/preview${query}`);
    },
    enabled: !!templateId,
    staleTime: 30_000
  });

  return {
    previewContent: data?.data?.previewContent || fallbackContent || '',
    isLoading: isLoading && !data,
    isFetching
  };
}

interface WhatsAppBubbleProps {
  content: string;
  isLoading?: boolean;
  documentFilename?: string;
  className?: string;
}

/** WhatsApp-style chat bubble used across the campaign wizard previews. Compact — no phone frame. */
export function WhatsAppBubble({ content, isLoading = false, documentFilename, className = '' }: WhatsAppBubbleProps) {
  return (
    <div className={`bg-[#e5ddd5] rounded-2xl p-4 ${className}`}>
      {/* Chat header */}
      <div className="mb-3">
        <GympeChatHeader compact />
      </div>

      {/* Message bubble */}
      <div className="bg-white rounded-lg rounded-tl-sm p-3 shadow-sm">
        {documentFilename && <DocumentPreviewCard filename={documentFilename} />}
        {isLoading ? (
          <div className="text-sm text-gray-400">Loading preview…</div>
        ) : (
          <div className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap break-words">
            {formatWhatsAppText(content)}
          </div>
        )}
        <div className="flex items-center justify-end gap-1 mt-1">
          <span className="text-[10px] text-gray-400">
            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
          <svg viewBox="0 0 18 10" className="w-4 h-3 text-sky-500" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M1 5.5 4 8.5 9.5 1.5" />
            <path d="M8 5.5 10.5 8.5 16.5 1.5" />
          </svg>
        </div>
      </div>
    </div>
  );
}
